import { USER_HAPPINESS_THRESHOLDS } from "./user-happiness-thresholds";
export var HappinessScore;
(function (HappinessScore) {
    HappinessScore[HappinessScore["Happy"] = 3] = "Happy";
    HappinessScore[HappinessScore["Okay"] = 2] = "Okay";
    HappinessScore[HappinessScore["Unhappy"] = 1] = "Unhappy";
})(HappinessScore || (HappinessScore = {}));
const METRIC_NAMES = {
    abandoned: "Page Abandoned",
    cls: "Cumulative Layout Shift",
    dcl: "DOM Content Loaded",
    fcp: "First Contentful Paint",
    inp: "Interaction to Next Paint",
    lcp: "Largest Contentful Paint",
    rage: "Rage Clicks",
    ttfb: "Time to First Byte",
};
export function getHappinessScoreLabel(score) {
    switch (score) {
        case HappinessScore.Happy:
            return "Happy";
        case HappinessScore.Okay:
            return "Okay";
        case HappinessScore.Unhappy:
            return "Unhappy";
        default:
            return "Unknown";
    }
}
/**
 * Expand the User Happiness reasons stored in a beacon to a more comprehensive object
 */
export function expandReasons(version, reasons) {
    if (!(version in USER_HAPPINESS_THRESHOLDS)) {
        return [];
    }
    const reasonsByMetric = Object.fromEntries(reasons.map((reason) => [reason.m, reason]));
    const thresholds = USER_HAPPINESS_THRESHOLDS[version].thresholds;
    return thresholds.map((config) => {
        let score = HappinessScore.Happy;
        let threshold = config.happy_value;
        if (config.key in reasonsByMetric) {
            const reason = reasonsByMetric[config.key];
            score = reason.s;
            threshold = reason.s === HappinessScore.Unhappy ? config.okay_value : config.happy_value;
        }
        return {
            key: config.key,
            metricName: getMetricName(config.key),
            columnName: config.column_name,
            score,
            threshold,
            inverseThreshold: config.inverse,
        };
    });
}
function getMetricName(metric) {
    return METRIC_NAMES[metric] || metric;
}
/**
 * Calculate the average of each happiness type from the given series, ensuring they all add up to
 * 100. The reason we can't just add the `stat` values up is because the `stat` is a percentile for
 * each series, so they might not add up to 100.
 */
export function getAverageHappiness(series) {
    const happyPageViews = sumDataByNum(series.find((s) => s.name.includes("Happy")));
    const okayPageViews = sumDataByNum(series.find((s) => s.name.includes("Okay")));
    const unhappyPageViews = sumDataByNum(series.find((s) => s.name.includes("Unhappy")));
    const totalPageViews = happyPageViews + okayPageViews + unhappyPageViews;
    const happiness = {
        happy: Math.round((happyPageViews / totalPageViews) * 100),
        okay: Math.round((okayPageViews / totalPageViews) * 100),
        unhappy: Math.round((unhappyPageViews / totalPageViews) * 100),
    };
    // double check that we add up to 100, rounding may have given us 99 or 101
    // adjust "okay" to get us 100%
    const total = happiness.happy + happiness.okay + happiness.unhappy;
    if (total === 99) {
        happiness.okay++;
    }
    else if (total === 101) {
        happiness.okay--;
    }
    return happiness;
}
function sumDataByNum(series) {
    if (series) {
        return series.data.reduce((total, item) => total + (item.num || 0), 0);
    }
    return 0;
}
export function applyHappinessColor(series) {
    var _a, _b, _c;
    if (String((_a = series.filters) === null || _a === void 0 ? void 0 : _a.userHappinessScores) === "3") {
        return Object.assign(Object.assign({}, series), { color: "var(--good-color)" });
    }
    if (String((_b = series.filters) === null || _b === void 0 ? void 0 : _b.userHappinessScores) === "2") {
        return Object.assign(Object.assign({}, series), { color: "var(--warning-color)" });
    }
    if (String((_c = series.filters) === null || _c === void 0 ? void 0 : _c.userHappinessScores) === "1") {
        return Object.assign(Object.assign({}, series), { color: "var(--bad-color)" });
    }
    return series;
}
